<template>
  <PageChrome>
    <template #title>Agent Settings</template>

    <template #actions>
      <span v-if="saving" class="autoSaveIndicator">Saving…</span>
      <span v-else-if="showSavedIndicator" class="autoSaveIndicator saved">Saved</span>
    </template>

    <div v-if="error" class="error">{{ error }}</div>

    <div v-if="showNoLlmBanner" class="warningBanner">
      <strong>No LLM provider connected.</strong>
      Connect either OpenAI or Anthropic on the
      <router-link to="/integrations">Integrations</router-link>
      page — agents and chat won't run until at least one is configured.
    </div>

    <div class="panel" style="max-width: 600px">

      <!-- Model selection -->
      <div class="section-heading">Models</div>

      <div class="field">
        <label>Agent model</label>
        <div class="webSearchSelect">
          <SearchSelect
            v-model="form.agent_model_id"
            :options="modelDropdownOptions"
            :clear-label="agentClearLabel"
            hide-search
          />
        </div>
        <div class="hint">Model used for background agent runs.</div>
      </div>

      <div class="field">
        <label>Chat model</label>
        <div class="webSearchSelect">
          <SearchSelect
            v-model="form.chat_model_id"
            :options="modelDropdownOptions"
            :clear-label="chatClearLabel"
            hide-search
          />
        </div>
        <div class="hint">Model used for the workspace chat.</div>
      </div>
    </div>
  </PageChrome>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import { apiFetch } from '../../services/api.ts'
import { useChatScope } from '../composables/useChatScope'

const { setScope, clearScope } = useChatScope()

const error = ref('')
const saving = ref(false)
const showSavedIndicator = ref(false)
let _savedTimer = null

const baselineSnapshot = ref('')
const models = ref([])  // [{ id, provider, prompt_per_1m, completion_per_1m, context_window }]
const defaults = ref({
  bootstrap_model_id: 'gpt-6-luna',
  openai_configured: true,
  anthropic_configured: false,
  exa_configured: false,
})

const form = reactive({
  agent_model_id: '',
  chat_model_id: '',
})

// Reactive clear-labels: each picker shows what would actually be used if its
// own field were blank, computed against the values currently in the form so
// editing one picker live-updates the other's "Default (...)" hint.
const bootstrapModelId = computed(() => defaults.value.bootstrap_model_id || 'gpt-6-luna')
const chatBootstrapModelId = computed(() => defaults.value.chat_model_id_default || bootstrapModelId.value)
const agentClearLabel = computed(() => `Default (${bootstrapModelId.value})`)
const chatClearLabel = computed(() => `Default (${chatBootstrapModelId.value})`)
const showNoLlmBanner = computed(() => !defaults.value.openai_configured && !defaults.value.anthropic_configured)

const modelDropdownOptions = computed(() => models.value.map((m) => ({
  value: m.id,
  label: m.id,
  secondaryLabel: `$${m.prompt_per_1m}/M in · $${m.completion_per_1m}/M out`,
  domain: m.provider === 'anthropic' ? 'claude.ai' : 'openai.com',
})))

function _snapshot() {
  return JSON.stringify({
    agent_model_id: form.agent_model_id || '',
    chat_model_id: form.chat_model_id || '',
  })
}

async function loadModels() {
  try {
    const resp = await apiFetch('/settings/models')
    models.value = Array.isArray(resp) ? resp : []
  } catch {
    models.value = []
  }
}

async function loadDefaults() {
  try {
    const resp = await apiFetch('/settings/defaults')
    if (resp && typeof resp === 'object') defaults.value = resp
  } catch {
    // keep last-known defaults
  }
}

async function reload() {
  error.value = ''
  try {
    const resp = await apiFetch('/settings/guardrails')
    form.agent_model_id = resp?.agent_model_id || defaults.value.agent_model_id_default || defaults.value.bootstrap_model_id || ''
    form.chat_model_id = resp?.chat_model_id || defaults.value.chat_model_id_default || defaults.value.bootstrap_model_id || ''
    baselineSnapshot.value = _snapshot()
  } catch (e) {
    error.value = e?.message || 'Failed to load settings'
  }
}

async function save() {
  saving.value = true
  error.value = ''
  showSavedIndicator.value = false
  if (_savedTimer) { clearTimeout(_savedTimer); _savedTimer = null }
  try {
    // Only send fields that changed from the baseline the server returned,
    // so we don't stamp env defaults into the tenant JSON and block future
    // default changes from being picked up.
    const baseline = baselineSnapshot.value ? JSON.parse(baselineSnapshot.value) : {}
    const body = {}
    const agentModel = form.agent_model_id || null
    const chatModel = form.chat_model_id || null
    if (agentModel !== (baseline.agent_model_id || null)) body.agent_model_id = agentModel
    if (chatModel !== (baseline.chat_model_id || null)) body.chat_model_id = chatModel

    await apiFetch('/settings/guardrails', {
      method: 'PUT',
      body,
    })
    baselineSnapshot.value = _snapshot()
    showSavedIndicator.value = true
    if (_savedTimer) clearTimeout(_savedTimer)
    _savedTimer = setTimeout(() => { showSavedIndicator.value = false }, 2000)
  } catch (e) {
    error.value = e?.message || 'Failed to save'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  loadModels()
  await loadDefaults()
  reload()
})

onBeforeUnmount(() => {
  clearScope()
})

let _autoSaveTimer = null

watch(
  () => _snapshot(),
  (cur) => {
    if (!baselineSnapshot.value) return
    if (cur === baselineSnapshot.value) return
    if (_autoSaveTimer) clearTimeout(_autoSaveTimer)
    _autoSaveTimer = setTimeout(() => {
      _autoSaveTimer = null
      save()
    }, 800)
  }
)
</script>

<style scoped>
.section-heading {
  font-size: 13px;
  font-weight: 700;
  color: var(--fg);
  margin-bottom: 14px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}

label {
  font-size: 13px;
  font-weight: 700;
  color: var(--muted);
}

.webSearchSelect {
  max-width: 320px;
}

.hint {
  color: var(--muted);
  font-size: 12px;
}

.error {
  margin-bottom: 10px;
  color: var(--danger);
}

.warningBanner {
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid var(--warning, #d97706);
  border-radius: 8px;
  background: var(--warning-bg, rgba(217, 119, 6, 0.08));
  color: var(--fg);
  font-size: 13px;
  line-height: 1.4;
}

.warningBanner strong {
  display: block;
  margin-bottom: 2px;
}

.warningBanner a {
  color: inherit;
  text-decoration: underline;
}

.autoSaveIndicator {
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
}

.autoSaveIndicator.saved {
  color: var(--success);
}
</style>
